/**
 * A card tried again and again, or one card at several charge points.
 *
 * OQ-32, in Lucian's words: one card refused thirteen times at three stations
 * inside an hour, and he wants to see that without reading rows, "so we can see
 * if someone is trying something". The by-station view is the other half: one
 * person trying many cards at one charge point, which is also what a charge
 * point with a broken reader looks like.
 *
 * THIS PAGE IS A LIST OF PSEUDONYMS, and that shapes every choice on it. A
 * blind index is not a name, but where a card was presented is movement data
 * about a person, and for a BMW card that person is an employee. The service
 * enforces the three rules that follow and this page does not get a say: only
 * cards with at least one refusal appear, the window is capped at seven days,
 * and nothing here exports.
 *
 * WHY THE FLAGS PRINT THEIR OWN RULE. The page says somebody may be trying
 * something, which is an accusation about a person. A reader has to be able to
 * argue with it, and a threshold they cannot see is one they cannot argue with.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Caption1,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { ArrowClockwiseRegular } from '@fluentui/react-icons'
import {
  Chip,
  DataTableShell,
  EmptyState,
  FilterBar,
  LoadingState,
  MonoText,
  SubTabs,
  formatTimestamp,
} from '../platform'
import { api } from '../api'
import type { AttemptsAnswer, AttemptCard, AttemptStation } from '../api'
import { asInstant, searchQuery } from './searchQuery'

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  filters: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  rules: {
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  row: {
    cursor: 'pointer',
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
  cardColumn: {
    width: '150px',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  countColumn: {
    width: '90px',
    whiteSpace: 'nowrap',
  },
  flagColumn: {
    width: '210px',
  },
  wordsColumn: {
    width: '230px',
    maxWidth: '230px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  timeColumn: {
    width: '175px',
    whiteSpace: 'nowrap',
  },
  // A placeholder is not a card. It stays in the list because how many charge
  // points still send one is worth seeing, and it is muted so it cannot be
  // read as somebody trying something (FIND-29).
  placeholder: {
    color: tokens.colorNeutralForeground3,
  },
})

/** The range, in the form the Decisions page uses, so the two agree. */
function defaultRange(): { from: string; to: string } {
  const now = new Date()
  const yesterday = new Date(now.getTime() - 24 * 3600 * 1000)
  return { from: toLocalInput(yesterday), to: toLocalInput(now) }
}

function toLocalInput(when: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}` +
    `T${pad(when.getHours())}:${pad(when.getMinutes())}`
  )
}

export default function Attempts() {
  const styles = useStyles()
  const [range, setRange] = useState(defaultRange)
  const [answer, setAnswer] = useState<AttemptsAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (asked: { from: string; to: string }) => {
    setError(null)
    try {
      const query = searchQuery({
        from: asInstant(asked.from),
        to: asInstant(asked.to),
      })
      setAnswer(await api.get<AttemptsAnswer>(`/attempts${query}`))
    } catch (failure) {
      setAnswer(null)
      setError(failure instanceof Error ? failure.message : String(failure))
    }
  }, [])

  useEffect(() => {
    void load(range)
  }, [load]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.page}>
      <FilterBar
        search={
          <div className={styles.filters}>
            <Input
              type="datetime-local"
              value={range.from}
              onChange={(_, d) => setRange({ ...range, from: d.value })}
              aria-label="From"
            />
            <Input
              type="datetime-local"
              value={range.to}
              onChange={(_, d) => setRange({ ...range, to: d.value })}
              aria-label="To"
            />
          </div>
        }
        actions={
          <Button icon={<ArrowClockwiseRegular />} onClick={() => void load(range)}>
            Show this range
          </Button>
        }
      />

      {error ? (
        <EmptyState
          title="This range was not answered"
          description={error}
        />
      ) : !answer ? (
        <LoadingState label="Counting attempts" />
      ) : (
        <SubTabs
          tabs={[
            {
              value: 'cards',
              label: `By card (${answer.cards.length})`,
              render: () => <CardsTable answer={answer} range={range} />,
            },
            {
              value: 'stations',
              label: `By charge point (${answer.stations.length})`,
              render: () => <StationsTable answer={answer} range={range} />,
            },
          ]}
        />
      )}
    </div>
  )
}

/**
 * What to print where a flag would be, per kind of non-card.
 *
 * THREE OF THESE SAY "NOT A CARD" AND THE FOURTH MUST NOT. `another form` is
 * Service OCPI's `other`: an identifier they did not recognise as a card and
 * did not classify as a placeholder, so DCS was never asked about it. Nothing
 * established that it is not a card, and if real cards are arriving in that
 * shape, saying "not a card" here is the page agreeing with the rule that is
 * wrong and hiding the evidence that it is.
 */
export function placeholderLabel(kind: string): string {
  if (kind === 'another form') return 'not asked at DCS: another form'
  if (kind === 'placeholder') return 'not a card: Service OCPI classified it'
  if (kind === 'station id') return "not a card: the charge point's own id"
  if (kind === 'literal') return 'not a card: a fixed word'
  return kind
}

/**
 * The counts under the table, including the one Service OCPI asked for.
 *
 * `other` is an identifier they did not recognise as a card and did not
 * classify as a placeholder, so DCS was never asked about it. If real cards
 * start arriving in that form, this number is the evidence and the rule that
 * has to change is theirs. It is on the page rather than in a query somebody
 * has to remember to run.
 */
function summarise(answer: AttemptsAnswer): string {
  const cards = answer.cards.length
  // NOT the `another form` rows: they are labelled "not asked at DCS" exactly
  // because nothing established they are not cards, and a count that folds
  // them in contradicts the label one column away.
  const notCards = answer.cards.filter(
    (card) => card.placeholder && card.placeholder.kind !== 'another form',
  ).length
  const another = answer.cards.filter((card) => card.kinds.includes('other')).length
  const parts = [`${cards} ${cards === 1 ? 'card' : 'cards'} refused at least once`]
  if (notCards) parts.push(`${notCards} of them not a card`)
  // Shown even at zero, because "none arrived in a form DCS is not asked
  // about" is the answer to their question and a missing line is not.
  parts.push(`${another} in a form DCS is not asked about`)
  return parts.join(', ')
}

/** The rules, printed where the flags are read rather than kept in a wiki. */
function Rules({ answer }: { answer: AttemptsAnswer }) {
  const styles = useStyles()
  return (
    <div className={styles.rules}>
      <Caption1>Keeps trying: {answer.rules.keeps_trying}.</Caption1>
      <Caption1>Several charge points: {answer.rules.several_stations}.</Caption1>
      <Caption1>{answer.rules.collapse}</Caption1>
      <Caption1>
        Only cards refused at least once in this range are listed, and the range is
        at most {answer.window.max_days} days. A card that was only ever approved is
        somebody doing their job.
      </Caption1>
    </div>
  )
}

function CardsTable({
  answer,
  range,
}: {
  answer: AttemptsAnswer
  range: { from: string; to: string }
}) {
  const styles = useStyles()
  const navigate = useNavigate()

  return (
    <DataTableShell
      title="By card"
      description="One row per card, most refused first. The card is shown as its blind index, which is how this service stores it: a pseudonym, not a number."
      footerStart={summarise(answer)}
      footerEnd={<Rules answer={answer} />}
    >
      {answer.cards.length === 0 ? (
        <EmptyState
          title="No card was refused in this range"
          description="Which is the answer, not an empty page: nothing in this window looked like somebody trying something."
        />
      ) : (
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={styles.cardColumn}>Card</TableHeaderCell>
              <TableHeaderCell className={styles.countColumn}>Refused</TableHeaderCell>
              <TableHeaderCell className={styles.countColumn}>Taps</TableHeaderCell>
              <TableHeaderCell className={styles.countColumn}>Stations</TableHeaderCell>
              <TableHeaderCell className={styles.flagColumn}>Flags</TableHeaderCell>
              <TableHeaderCell className={styles.wordsColumn}>Why, as the authorities put it</TableHeaderCell>
              <TableHeaderCell className={styles.timeColumn}>Last seen, Berlin</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {answer.cards.map((card) => (
              <CardRow
                key={card.card}
                card={card}
                onOpen={() => navigate(decisionsFor({ card: card.card }, range))}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </DataTableShell>
  )
}

export function CardRow({ card, onOpen }: { card: AttemptCard; onOpen: () => void }) {
  const styles = useStyles()
  const words = [...card.reasons, ...card.decided_by].map((w) => w.word)
  return (
    <TableRow
      className={styles.row}
      tabIndex={0}
      aria-label={`Open the decisions for card ${card.card.slice(0, 12)}`}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        onOpen()
      }}
    >
      <TableCell className={styles.cardColumn}>
        <MonoText tone="muted">{card.card.slice(0, 16)}</MonoText>
      </TableCell>
      <TableCell className={styles.countColumn}>{card.refused}</TableCell>
      <TableCell className={styles.countColumn}>{card.taps}</TableCell>
      <TableCell className={styles.countColumn}>{card.refused_stations}</TableCell>
      <TableCell className={styles.flagColumn}>
        {card.placeholder ? (
          // Not a card, so no flag. Said in words rather than left blank: an
          // empty cell here reads as "nothing to say about this one", and what
          // the row needs is "this is a charge point, not a person".
          <span className={styles.placeholder} title={card.placeholder.detail}>
            {placeholderLabel(card.placeholder.kind)}
          </span>
        ) : (
          <>
            {card.keeps_trying ? <Chip selected>keeps trying</Chip> : null}
            {card.several_stations ? <Chip selected>several charge points</Chip> : null}
          </>
        )}
      </TableCell>
      <TableCell className={styles.wordsColumn} title={words.join(', ')}>
        {words.length ? words.join(', ') : <MonoText tone="muted">not recorded</MonoText>}
      </TableCell>
      <TableCell className={mergeClasses(styles.timeColumn, styles.nowrap)}>
        <MonoText>{formatTimestamp(card.last_seen) || 'no time recorded'}</MonoText>
      </TableCell>
    </TableRow>
  )
}

function StationsTable({
  answer,
  range,
}: {
  answer: AttemptsAnswer
  range: { from: string; to: string }
}) {
  const styles = useStyles()
  const navigate = useNavigate()

  return (
    <DataTableShell
      title="By charge point"
      description="One row per charge point that refused a card, most distinct cards first. Many cards refused at one charge point is a person trying several, or a reader that has stopped working."
      footerStart={`${answer.stations.length} ${answer.stations.length === 1 ? 'charge point' : 'charge points'} refused a card`}
    >
      {answer.stations.length === 0 ? (
        <EmptyState
          title="No charge point refused a card in this range"
          description="Nothing was refused anywhere in this window."
        />
      ) : (
        <Table size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Charge point</TableHeaderCell>
              <TableHeaderCell className={styles.countColumn}>Cards refused</TableHeaderCell>
              <TableHeaderCell className={styles.countColumn}>Refused taps</TableHeaderCell>
              <TableHeaderCell className={styles.wordsColumn}>Most frequent reason</TableHeaderCell>
              <TableHeaderCell className={styles.timeColumn}>Last seen, Berlin</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {answer.stations.map((station) => (
              <StationRow
                key={station.station_id}
                station={station}
                onOpen={() =>
                  navigate(decisionsFor({ station: station.station_id }, range))
                }
              />
            ))}
          </TableBody>
        </Table>
      )}
    </DataTableShell>
  )
}

export function StationRow({
  station,
  onOpen,
}: {
  station: AttemptStation
  onOpen: () => void
}) {
  const styles = useStyles()
  return (
    <TableRow
      className={styles.row}
      tabIndex={0}
      aria-label={`Open the decisions for charge point ${station.station_id}`}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        onOpen()
      }}
    >
      <TableCell>
        <MonoText>{station.station_id}</MonoText>
      </TableCell>
      <TableCell className={styles.countColumn}>{station.cards_refused}</TableCell>
      <TableCell className={styles.countColumn}>{station.refused}</TableCell>
      <TableCell className={styles.wordsColumn} title={station.common_reason ?? ''}>
        {station.common_reason ?? <MonoText tone="muted">not recorded</MonoText>}
      </TableCell>
      <TableCell className={mergeClasses(styles.timeColumn, styles.nowrap)}>
        <MonoText>{formatTimestamp(station.last_seen) || 'no time recorded'}</MonoText>
      </TableCell>
    </TableRow>
  )
}

/**
 * The address of the Decisions page, already filtered to this row and this
 * range.
 *
 * THE INDEX TRAVELS IN THE URL AND NOWHERE ELSE. Decisions can be opened by
 * blind index because this page produces the address; there is deliberately no
 * box on that page for a person to type one into. An index is a pseudonym, and
 * a search by pseudonym that anybody can run by hand is a different feature
 * from following a row you are already looking at.
 */
export function decisionsFor(
  row: { card?: string; station?: string },
  range: { from: string; to: string },
): string {
  return `/decisions${searchQuery({
    identifier_blind_index: row.card,
    station_id: row.station,
    from: asInstant(range.from),
    to: asInstant(range.to),
  })}`
}
